import { EstateForSalePageFragment } from '@gql'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EstateSettingRoundDates } from './EstateSettingRoundDates'
import { EstateSettingRoundOptions } from './EstateSettingRoundOptions'
import { EstateSettingNewRoundForm } from './EstateSettingNewRoundForm'
import { InfoIcon, RefreshCcwDotIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

type EstateSettingCurrentRoundTabProps = {
  estate: Pick<
    EstateForSalePageFragment,
    | 'id'
    | 'firstPrice'
    | 'reservePrice'
    | 'estateRounds'
    | 'currentRoundId'
    | 'isPublished'
  >
  /** Refers only to the permission system (not core logic) */
  readOnly?: boolean
}

export const EstateSettingCurrentRoundTab = ({
  estate,
  readOnly,
}: EstateSettingCurrentRoundTabProps) => {
  const { t } = useTranslation()
  const [isNewRoundModalOpen, setIsNewRoundModalOpen] = useState(false)

  const currentRound = useMemo(() => {
    return estate.estateRounds.find(
      (round) => round.id === estate.currentRoundId
    )
  }, [estate.estateRounds, estate.currentRoundId])

  const isCurrentRoundPublic = useMemo(() => {
    return currentRound?.isPublic === true
  }, [currentRound])

  const isCurrentRoundStillOpen = useMemo(() => {
    return currentRound && new Date(currentRound?.endDate) > new Date()
  }, [currentRound])

  return (
    <div className="tw-space-y-6 tw-h-full">
      {currentRound && (
        <>
          {isCurrentRoundPublic && isCurrentRoundStillOpen && (
            <div className="tw-py-3 tw-p-4 tw-bg-green-200 tw-rounded-lg">
              <p className="tw-text-sm tw-font-normal tw-text-green-700 tw-flex tw-items-center">
                <InfoIcon strokeWidth={3} className="tw-mr-3" />
                {t('EstateSettingCurrentRound.firstRoundStillOpen')}
              </p>
            </div>
          )}

          <>
            <h2 className="tw-text-lg tw-font-semibold">
              {t('EstateSettingCurrentRound.title', {
                number: currentRound.number,
              })}
            </h2>

            <EstateSettingRoundOptions
              round={currentRound}
              readOnly={readOnly || !isCurrentRoundStillOpen}
            />

            <EstateSettingRoundDates
              round={currentRound}
              readOnly={readOnly}
              canEditStartDate={false}
              canEditEndDate={isCurrentRoundStillOpen}
            />
          </>

          {!isCurrentRoundStillOpen && (
            <>
              <div className="tw-mt-3 tw-bg-zinc-50 tw-text-zinc-500 tw-flex tw-items-start tw-p-5 tw-justify-between tw-gap-3">
                <div>
                  <RefreshCcwDotIcon />
                </div>
                <div className="tw-flex tw-flex-col tw-gap-2">
                  <div>
                    <p className="tw-font-medium">
                      {t('EstateSettingOffersForm.infoCard.title')}
                    </p>
                    <p className="tw-text-xs">
                      {t('EstateSettingOffersForm.infoCard.subTitle')}
                    </p>
                  </div>
                  <div>
                    <p className="tw-text-sm">
                      {t('EstateSettingOffersForm.infoCard.description')}
                    </p>
                  </div>
                </div>
                <Button
                  disabled={isCurrentRoundStillOpen || readOnly}
                  className="tw-self-end"
                  size="sm"
                  onClick={() => setIsNewRoundModalOpen(true)}
                >
                  {t('EstateSettingOffersForm.infoCard.button')}
                </Button>
              </div>

              <Dialog
                open={isNewRoundModalOpen}
                onOpenChange={setIsNewRoundModalOpen}
              >
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>
                      {t('EstateSettingOffersForm.modal.title')}
                    </DialogTitle>
                    <DialogDescription>
                      {t('EstateSettingOffersForm.modal.description')}
                    </DialogDescription>
                  </DialogHeader>
                  <EstateSettingNewRoundForm
                    estateId={estate.id}
                    currentRound={currentRound}
                    readOnly={readOnly}
                    onSuccess={() => setIsNewRoundModalOpen(false)}
                  />
                </DialogContent>
              </Dialog>
            </>
          )}
        </>
      )}
    </div>
  )
}
